import React, {useCallback, useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {Column} from "primereact/column";
import {DataTable} from "primereact/datatable";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {Tooltip} from "primereact/tooltip";

import {SellerWorkspaceService} from "../../service/SellerWorkspaceService";
import SellerExport from "./SellerExport";
import SellerIdentityChips from "./SellerIdentityChips";
import SellerLinkEditor from "./SellerLinkEditor";
import {sellerChips} from "./sellerIdentity";
import {formatCount} from "./sellerFormat";

const sellerWorkspaceService = new SellerWorkspaceService();
const PAGE_SIZE = 50;
const SEARCH_DELAY_MS = 300;
// PrimeReact shows an option whose value is null as an empty box.
const ALL = 'all';

// A store total from a link nobody reviewed says so, as its chip does.
const StoreCount = ({t, row, marketplaceKey}) => {
    const value = row.counts?.[marketplaceKey];
    const automatic = row.view?.links?.[marketplaceKey]?.member?.status === 'auto';
    return <span style={value === null || value === undefined ? {color: '#6b7280'} : undefined}>
        {formatCount(value, t)}
        {automatic && value !== null && value !== undefined &&
            <small style={{color: '#6b7280', marginLeft: 4}}>({t('onboarding.sites.identity.automatic', 'otomatik')})</small>}
    </span>;
};

// Level 2: one row per seller on the operator's own marketplace, paged on the server.
const SellerList = ({t, toast, onError, marketplace, filter, category, onFilters, onOpenSeller, reloadKey}) => {
    const [answer, setAnswer] = useState(null);
    const [failed, setFailed] = useState(false);
    const [loading, setLoading] = useState(false);
    const [first, setFirst] = useState(0);
    const [search, setSearch] = useState('');
    const [query, setQuery] = useState('');
    const [sort, setSort] = useState('count');
    const [editing, setEditing] = useState(null);   // {storeId, key}; the chip itself is re-read after each reload
    const latest = useRef(0);

    useEffect(() => {
        const timer = setTimeout(() => { setQuery(search.trim()); setFirst(0); }, search ? SEARCH_DELAY_MS : 0);
        return () => clearTimeout(timer);
    }, [search]);
    useEffect(() => { setFirst(0); }, [marketplace, filter, category]);

    const load = useCallback(() => {
        const request = ++latest.current;
        setLoading(true);
        return sellerWorkspaceService.getSellers({first, rows: PAGE_SIZE, q: query, filter, marketplace, category, sort})
            .then(found => { if (request === latest.current) { setAnswer(found); setFailed(false); } })
            .catch(() => { if (request === latest.current) setFailed(true); })
            .finally(() => { if (request === latest.current) setLoading(false); });
    }, [first, query, filter, marketplace, category, sort]);
    useEffect(() => { load(); }, [load, reloadKey]);

    const marketplaces = answer?.marketplaces || [];
    const rows = answer?.rows || [];
    const editedRow = editing && rows.find(row => row.store_id === editing.storeId);
    const editedChip = editedRow ? sellerChips(editedRow.view, marketplaces).find(chip => chip.key === editing.key) : null;

    return <div>
        <div style={{display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10}}>
            <Dropdown value={filter || ALL} style={{width: '16em'}}
                      options={[{label: t('sellers_workspace.filter.all', 'Tüm satıcılar'), value: ALL},
                                {label: t('sellers_workspace.filter.unmatched', 'Eşleşmeyenler'), value: 'unmatched'},
                                {label: t('sellers_workspace.filter.suggestions', 'Onay bekleyen öneriler'), value: 'suggestions'},
                                {label: t('sellers_workspace.filter.auto', 'Otomatik (incelenmedi)'), value: 'auto'},
                                {label: t('sellers_workspace.filter.officialDifferent', 'Resmi kayıt farklı'), value: 'official_different'},
                                {label: t('sellers_workspace.filter.strong', 'Güçlü (incelenmedi)'), value: 'strong'},
                                {label: t('sellers_workspace.filter.likely', 'Olası (incelenmedi)'), value: 'likely'},
                                {label: t('sellers_workspace.filter.weak', 'Zayıf (incelenmedi)'), value: 'weak'}]}
                      onChange={(e) => onFilters({filter: e.value === ALL ? null : e.value})}/>
            <Dropdown value={marketplace || ALL} style={{width: '13em'}}
                      options={[{label: t('sellers_workspace.filter.allMarketplaces', 'Tüm pazaryerleri'), value: ALL},
                                ...marketplaces.map(item => ({label: item.label, value: item.key}))]}
                      onChange={(e) => onFilters({marketplace: e.value === ALL ? null : e.value})}/>
            <Dropdown value={category || ALL} style={{width: '15em'}}
                      options={[{label: t('sellers_workspace.filter.allCategories', 'Tüm Koçtaş kategorileri'), value: ALL},
                                ...(answer?.categories || []).map(name => ({label: name, value: name}))]}
                      onChange={(e) => onFilters({category: e.value === ALL ? null : e.value})}/>
            <Dropdown value={sort} style={{width: '13em'}}
                      options={[{label: t('sellers_workspace.sort.count', 'Ürün sayısına göre'), value: 'count'},
                                {label: t('sellers_workspace.sort.name', 'Ada göre'), value: 'name'}]}
                      onChange={(e) => { setSort(e.value); setFirst(0); }}/>
            <span className="p-input-icon-left" style={{width: '16em'}}>
                <i className="pi pi-search"/>
                <InputText value={search} style={{width: '100%'}} placeholder={t('sellers_workspace.search', 'Satıcı ara')}
                           onChange={(e) => setSearch(e.target.value)}/>
            </span>
            <SellerExport t={t} onError={onError} reloadKey={reloadKey}/>
        </div>
        {failed &&
            <Message severity="warn" style={{display: 'block', marginBottom: 10}}
                     text={t('sellers_workspace.loadFailed', 'Satıcı bilgileri yüklenemedi.')}/>}
        <Tooltip target=".seller-identity-chip" position="top"/>
        <DataTable value={rows} dataKey="store_id" responsiveLayout="scroll" lazy paginator loading={loading}
                   first={first} rows={PAGE_SIZE} totalRecords={answer?.total ?? 0} onPage={(e) => setFirst(e.first)}
                   emptyMessage={t('sellers_workspace.list.empty', 'Bu filtreye uyan satıcı yok.')}>
            <Column header={t('sellers_workspace.list.seller', 'Koçtaş satıcısı')}
                    body={(row) => <span style={{whiteSpace: 'nowrap'}}>
                        <Button label={row.name} className="p-button-link" style={{padding: 0}} onClick={() => onOpenSeller(row.store_id)}/>
                        {row.store_url &&
                            <a href={row.store_url} target="_blank" rel="noopener noreferrer" style={{marginLeft: 6}}
                               title={t('onboarding.sites.identity.openStore', 'Mağazayı aç')}>
                                <i className="pi pi-external-link" style={{fontSize: '0.75rem'}}/>
                            </a>}
                    </span>}/>
            <Column header={t('sellers_workspace.list.others', 'Diğer pazaryerlerinde')} style={{minWidth: '22em'}}
                    body={(row) => <SellerIdentityChips t={t} view={row.view} marketplaces={marketplaces} notFoundOn={row.not_found_on}
                                                        onChipClick={(chip) => setEditing({storeId: row.store_id, key: chip.key})}/>}/>
            <Column header={t('sellers_workspace.list.koctasCount', 'Koçtaş ürün')} body={(row) => <StoreCount t={t} row={row} marketplaceKey="koctas"/>}/>
            <Column header={t('sellers_workspace.list.trendyolCount', 'Trendyol toplam')} body={(row) => <StoreCount t={t} row={row} marketplaceKey="trendyol"/>}/>
            <Column header={t('sellers_workspace.list.hepsiburadaCount', 'Hepsiburada toplam')} body={(row) => <StoreCount t={t} row={row} marketplaceKey="hepsiburada"/>}/>
            <Column header={t('sellers_workspace.list.tracked', 'İzlenen ürün')} body={(row) => formatCount(row.tracked, t)}/>
        </DataTable>
        <p style={{color: '#6b7280', marginTop: 10}}>
            {t('sellers_workspace.list.note', 'Trendyol ve Hepsiburada sütunları mağaza toplamıdır; kategori filtresi yalnız Koçtaş satıcılarını daraltır. Eşleşme yoksa veya sayı okunamadıysa "bilinmiyor" yazar. Toplamların farkı eksik ürün demek değildir: siteler varyantları farklı gruplar.')}
        </p>
        {editedChip &&
            <SellerLinkEditor t={t} toast={toast} onError={onError} chip={editedChip} onChanged={load}
                              seller={{marketplace_key: editedRow.view.marketplace_key, seller_name: editedRow.view.seller_name}}
                              onClose={() => setEditing(null)}/>}
    </div>;
};

export default SellerList;
